import { createContext, type ReactNode, useContext } from 'react';

interface RowContextValue {
  rowId: string;
  setHoveredRowId: (value: string | null) => void;
  isHovered: boolean;
}

/**
 * The history table is a CSS grid: each logical row is several sibling grid cells laid out
 * side-by-side, so they cannot share a single hoverable wrapper element. This context synchronizes
 * hover highlighting across every cell of one logical row — the outer <Row> supplies the hovered-id
 * setter and the precomputed `isHovered` flag via context, and each inner <Cell> calls the setter on
 * mouse enter/leave and reads `isHovered` to toggle its own background. (Default value is a no-op so
 *  a <Cell> rendered outside a <Row> degrades safely rather than throwing.)
 */
const RowContext = createContext<RowContextValue>({
  rowId: '',
  setHoveredRowId: () => {},
  isHovered: false,
});

export const useRowContext = (): RowContextValue => useContext(RowContext);

interface RowProps {
  rowId: string;
  hoveredRowId: string | null;
  setHoveredRowId: (value: string | null) => void;
  children: ReactNode;
}

export const Row = ({ rowId, hoveredRowId, setHoveredRowId, children }: RowProps) => (
  <RowContext.Provider value={{ rowId, setHoveredRowId, isHovered: hoveredRowId === rowId }}>
    {children}
  </RowContext.Provider>
);
